import React, { useEffect, useMemo, useState } from "react";
import { TableRow } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";

import { StyledTableCell } from "../../shared/components/CustomTable/Styled";
import ListView from "../../shared/components/ListView";
import { api } from "../../shared/hooks/api";
import { getBaseUrl } from "../../utils/api";
import { getStoredAuthToken } from "../../utils/authToken";
import { AuditFilterType, AuditSortTypes } from "../../configs/audit";
import { color } from "../../shared/styles";

import JsonView from "./JsonView";
import HelpModal from "./HelpModal";
import AuditFilterGuide from "./FilterGuide";
import {
    DetailsCell,
    DetailsWrapper,
    ExportBar,
    ExpandCell,
    Label,
    SectionBody,
    SectionButton,
    SectionButtonPrimary,
    SectionCard,
    SectionHeader,
    SectionTitle,
    Value,
} from "./Styled";

async function downloadWithAuth(url: string, filename: string) {
    const token = getStoredAuthToken();
    const res = await fetch(url, {
        headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
    });
    if (!res.ok) {
        const text = await res.text();
        throw new Error(`Download failed (${res.status}): ${text}`);
    }
    const blob = await res.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(blobUrl);
}

const ParentTableColumns = [
    { text: "", icon: <></> },
    { text: "Time", icon: <></> },
    { text: "Actions", icon: <></> },
    { text: "Codes", icon: <></> },
    { text: "Actor", icon: <></> },
    { text: "Request", icon: <></> },
    { text: "Count", icon: <></> },
];

const ChildTableHeader: React.FC = () => (
    <TableRow>
        <StyledTableCell />
        <StyledTableCell>Time</StyledTableCell>
        <StyledTableCell>Action</StyledTableCell>
        <StyledTableCell>Actor</StyledTableCell>
        <StyledTableCell>Entity</StyledTableCell>
        <StyledTableCell>Entity code</StyledTableCell>
        <StyledTableCell>Request</StyledTableCell>
    </TableRow>
);

const ChildEventRow: React.FC<{ item: any }> = ({ item }) => {
    const [expanded, setExpanded] = useState(false);

    const filteredMetadata = useMemo(() => {
        const md = item?.metadata || {};
        const { root_snapshot, ...rest } = md;
        return rest;
    }, [item]);

    const entityCode =
        item?.metadata?.line_snapshot?.material_code ||
        item?.metadata?.line_snapshot?.product_code ||
        item?.metadata?.code ||
        item?.metadata?.root_snapshot?.order_name ||
        item?.entity_label ||
        `${item.entity_type}.${item.entity_id}`;

    return (
        <>
            <TableRow>
                <ExpandCell onClick={() => setExpanded((s) => !s)}>
                    {expanded ? "▾" : "▸"}
                </ExpandCell>
                <StyledTableCell>
                    {new Date(item.occurred_at).toLocaleString()}
                </StyledTableCell>
                <StyledTableCell>{item.action}</StyledTableCell>
                <StyledTableCell>
                    {item.actor_label || item.actor_email || item.actor_id || "-"}
                </StyledTableCell>
                <StyledTableCell>
                    {item.entity_label || `${item.entity_type}.${item.entity_id}`}
                </StyledTableCell>
                <StyledTableCell>{entityCode}</StyledTableCell>
                <StyledTableCell>{item.request_id}</StyledTableCell>
            </TableRow>
            {expanded && (
                <TableRow>
                    <DetailsCell colSpan={ParentTableColumns.length}>
                        <DetailsWrapper>
                            <div>
                                <Label>Root</Label>
                                <Value>
                                    {item.root_entity_label ||
                                        (item.root_entity_type
                                            ? `${item.root_entity_type}.${item.root_entity_id}`
                                            : "-")}
                                </Value>
                            </div>
                            <div>
                                <Label>Entity</Label>
                                <Value>{`${item.entity_type}.${item.entity_id}`}</Value>
                            </div>
                            <div style={{ gridColumn: "1 / -1" }}>
                                <Label>Changes</Label>
                                <JsonView data={item.changes} />
                            </div>
                            <div style={{ gridColumn: "1 / -1" }}>
                                <Label>Metadata</Label>
                                <JsonView data={filteredMetadata} />
                            </div>
                        </DetailsWrapper>
                    </DetailsCell>
                </TableRow>
            )}
        </>
    );
};

const RequestRow: React.FC<{ item: any }> = ({ item }) => {
    const [expanded, setExpanded] = useState(false);
    const [{ data }, fetchChildren] = api.get<any>(
        `/audit/requests/${item.request_id}/events/`,
        {},
        true,
    );

    const childEvents = Array.isArray(data)
        ? data
        : Array.isArray(data?.results)
        ? data.results
        : [];

    const rootEvent = useMemo(
        () => (childEvents || []).find((ev: any) => ev?.metadata?.is_root),
        [childEvents],
    );
    const rootSnapshot = rootEvent?.metadata?.root_snapshot;

    useEffect(() => {
        if (expanded) fetchChildren();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [expanded]);

    const timeRange = `${new Date(item.occurred_at_min).toLocaleString()} → ${new Date(
        item.occurred_at_max,
    ).toLocaleString()}`;

    return (
        <>
            <TableRow>
                <ExpandCell onClick={() => setExpanded((s) => !s)}>
                    {expanded ? "▾" : "▸"}
                </ExpandCell>
                <StyledTableCell>{timeRange}</StyledTableCell>
                <StyledTableCell>
                    {(item.actions || []).join(", ")}
                </StyledTableCell>
                <StyledTableCell>{(item.codes || []).join(", ")}</StyledTableCell>
                <StyledTableCell>
                    {item.actor_email || item.actor_id || "-"}
                </StyledTableCell>
                <StyledTableCell>{item.request_id}</StyledTableCell>
                <StyledTableCell>{item.total_events}</StyledTableCell>
            </TableRow>
            {expanded && (
                <TableRow>
                    <DetailsCell colSpan={ParentTableColumns.length}>
                        {rootSnapshot && (
                            <div style={{ marginBottom: 8 }}>
                                <Label>Root snapshot</Label>
                                <JsonView
                                    data={rootSnapshot}
                                    borderLeftColor={color.primary.BLUE_B200}
                                />
                            </div>
                        )}
                        <ChildTableHeader />
                        {childEvents.map((ev: any) => (
                            <ChildEventRow key={ev.id} item={ev} />
                        ))}
                    </DetailsCell>
                </TableRow>
            )}
        </>
    );
};

const Audit: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [showHelp, setShowHelp] = useState(false);
    const [presetApplied, setPresetApplied] = useState(false);

    const exportFiltered = async () => {
        const url = `${getBaseUrl()}/audit/events/export/${location.search || ""}`;
        await downloadWithAuth(url, "audit_export.csv");
    };

    const setQuickFilters = (params: Record<string, string | undefined>) => {
        const search = new URLSearchParams(location.search);
        Object.entries(params).forEach(([key, value]) => {
            if (value === undefined) {
                search.delete(key);
                return;
            }
            search.set(key, value);
        });
        search.delete("page");
        const query = search.toString();
        navigate(query ? `${location.pathname}?${query}` : location.pathname);
    };

    const setPresetFilters = (params: Record<string, string | undefined>) => {
        const search = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value === undefined) return;
            search.set(key, value);
        });
        const query = search.toString();
        setPresetApplied(true);
        navigate(query ? `${location.pathname}?${query}` : location.pathname);
    };

    const applyLastDays = (days: number) => {
        const end = new Date();
        const start = new Date();
        start.setDate(end.getDate() - days);
        setQuickFilters({
            occurred_at__gte: start.toISOString(),
            occurred_at__lte: end.toISOString(),
        });
    };

    useEffect(() => {
        if (!location.search && !presetApplied) {
            setPresetFilters({
                action_exclude_prefix: "auth.",
                action_exclude: "bulk_upload.completed",
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search, presetApplied]);

    return (
        <>
            <AuditFilterGuide />
            <SectionCard>
                <SectionHeader>
                    <SectionTitle>Bộ lọc nhanh / Quick filters</SectionTitle>
                </SectionHeader>
                <SectionBody>
                    <SectionButton onClick={() => applyLastDays(7)}>
                        Last 7 days
                    </SectionButton>
                    <SectionButton onClick={() => applyLastDays(30)}>
                        Last 30 days
                    </SectionButton>
                    <SectionButton
                        onClick={() => setQuickFilters({ is_root: "true" })}
                    >
                        Root only
                    </SectionButton>
                    <SectionButton
                        onClick={() =>
                            setQuickFilters({
                                occurred_at__gte: undefined,
                                occurred_at__lte: undefined,
                                is_root: undefined,
                            })
                        }
                    >
                        Clear quick filters
                    </SectionButton>
                </SectionBody>
            </SectionCard>
            <SectionCard>
                <SectionHeader>
                    <SectionTitle>Presets</SectionTitle>
                </SectionHeader>
                <SectionBody>
                    <SectionButton
                        onClick={() =>
                            setPresetFilters({
                                action_exclude_prefix: "auth.",
                                action_exclude: "bulk_upload.completed",
                            })
                        }
                    >
                        Transactional (default)
                    </SectionButton>
                    <SectionButton
                        onClick={() =>
                            setPresetFilters({
                                action_prefix: "auth.",
                            })
                        }
                    >
                        Auth audit
                    </SectionButton>
                    <SectionButton
                        onClick={() =>
                            setPresetFilters({
                                action: "bulk_upload.completed",
                            })
                        }
                    >
                        Import/Upload
                    </SectionButton>
                    <SectionButton
                        onClick={() =>
                            setPresetFilters({
                                root_entity_type: "product_flow.saleorder",
                            })
                        }
                    >
                        Sales order
                    </SectionButton>
                    <SectionButton
                        onClick={() =>
                            setPresetFilters({
                                entity_type:
                                    "materials.materialtransaction,products.producttransaction",
                            })
                        }
                    >
                        Inventory IN/OUT
                    </SectionButton>
                    <SectionButton onClick={() => setPresetFilters({})}>
                        All events
                    </SectionButton>
                </SectionBody>
            </SectionCard>
            <ExportBar>
                <SectionButtonPrimary onClick={exportFiltered}>
                    Export (filtered)
                </SectionButtonPrimary>
                <SectionButton onClick={() => setShowHelp(true)}>
                    How it works
                </SectionButton>
            </ExportBar>
            <ListView
                url="/audit/requests/"
                rowElement={RequestRow}
                tableColumns={ParentTableColumns as any}
                textString="Search audit requests"
                sortTypes={AuditSortTypes}
                filterTypes={AuditFilterType}
                enableUrlRefresh
                skipEmptyUrlRefresh
            />
            <HelpModal open={showHelp} onClose={() => setShowHelp(false)} />
        </>
    );
};

export default Audit;
